import { AssetBonusDisplay } from '@/components/AssetBonusDisplay';
import { AssetIcon } from '@/components/AssetIcon';
import { NavIcon } from '@/components/icons/NavIcon';
import { Card } from '@/components/ui/card';
import {
  getGuideBonusesData,
  type BonusDefinition,
  type BonusEntry,
} from '@/lib/bonuses-client';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';

const dragonCards = [
  {
    title: 'Starting a Dragon',
    points: [
      'Only a monarch or co-monarch can start or cancel a dragon project.',
      'Protection blocks dragon projects, and a kingdom can only work on one project at a time.',
      'The selected dragon type must be available in the current age.',
    ],
  },
  {
    title: 'Target Rules',
    points: [
      'You cannot target your own kingdom.',
      'Dragons are always allowed in war or at Unfriendly and above.',
      'Outside that, the target kingdom must be within roughly 50% to 200% of your kingdom networth.',
    ],
  },
  {
    title: 'Funding and Launch',
    points: [
      'A project is funded by province donations until the gold requirement reaches zero.',
      'The final send checks stance and relation blockers, so a nearly finished dragon can pause at 1gc remaining if the target becomes invalid.',
      'After launch, the dragon flies for 6 days before it expires unless it is slain first.',
    ],
  },
];

const slayingRows = [
  ['Soldiers', '1 damage each'],
  ['Offensive specialists', 'Their full offense value as damage'],
  ['Defensive specialists', 'Their full defense value as damage'],
  ['Thieves', '5 damage each when your province has thief-on-dragon access'],
  ['Wizards', '5 damage each when your province has wizard-on-dragon access'],
];

function DragonCard({
  dragon,
  bonusDefinitionsById,
}: {
  dragon: BonusEntry;
  bonusDefinitionsById: Map<number, BonusDefinition>;
}) {
  return (
    <Card className="p-4">
      <div className="flex flex-col gap-4 sm:flex-row">
        <AssetIcon assetCode={dragon.name} assetType="dragon" size="small" />
        <div className="min-w-0 flex-1 space-y-3">
          <div>
            <h3 className="font-cinzel text-lg text-[#f0f0f0]">{dragon.name}</h3>
            <p className="mt-1 text-sm text-[#9f9f9f]">Live effect list from the current age bonus bundle.</p>
          </div>
          {dragon.bonusDetails.length ? (
            <AssetBonusDisplay asset={dragon} bonusDefinitionsById={bonusDefinitionsById} />
          ) : (
            <p className="text-sm text-[#b8b8b8]">No published passive bonus lines are attached to this dragon in the current age bundle.</p>
          )}
        </div>
      </div>
    </Card>
  );
}

export const Route = createFileRoute('/classic-guide/dragons')({
  component: ClassicGuideDragonsPage,
});

function ClassicGuideDragonsPage() {
  const bonusesQuery = useQuery({
    queryKey: ['guide-bonuses', 'dragons'],
    queryFn: getGuideBonusesData,
    staleTime: 1000 * 60 * 10,
  });

  const dragons = bonusesQuery.data?.dragons || [];
  const bonusDefinitionsById = new Map(
    (bonusesQuery.data?.bonusDefinitions || []).map(definition => [definition.id, definition]),
  );

  return (
    <div className="space-y-5">
      <Card className="p-6">
        <div className="flex items-center gap-4">
          <NavIcon
            code="dragons"
            width={72}
            height={72}
            className="shrink-0 overflow-hidden rounded-xl border border-[#d4af37]/25 shadow-[0_0_18px_rgba(212,175,55,0.16)]"
          />
          <div className="min-w-0">
            <h2 className="font-cinzel text-3xl text-[#f0f0f0]">Dragons</h2>
            <p className="mt-1 text-base leading-7 text-[#b8b8b8]">
              Dragon projects, slaying rules and dragon effects.
            </p>
          </div>
        </div>
      </Card>

      <Card className="space-y-5 p-6">
        {dragonCards.map(card => (
          <div key={card.title} className="space-y-3">
            <h3 className="font-cinzel text-2xl text-[#e6c547]">{card.title}</h3>
            <ul className="space-y-2 rounded-lg border border-[#d4af37]/15 bg-[#050505]/50 px-5 py-4 text-[1.0625rem] leading-7 text-[#e8dfcf]">
              {card.points.map(point => (
                <li key={point} className="flex gap-3">
                  <span className="mt-[0.65rem] h-1.5 w-1.5 shrink-0 rounded-full bg-[#e6c547]" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div className="space-y-4">
          <h3 className="font-cinzel text-2xl text-[#e6c547]">Dragon Types</h3>
          <p className="text-[1.0625rem] leading-8 text-[#e3e3e3]">
            These dragon effects are taken from the current age data.
          </p>

          {bonusesQuery.isLoading ? <p className="text-sm text-[#b8b8b8]">Loading current age dragons...</p> : null}
          {bonusesQuery.isError ? (
            <p className="text-sm text-[#b8b8b8]">
              Current age dragon data could not be loaded. The guide app needs the published bonuses
              and strings bundle to render this page.
            </p>
          ) : null}

          {bonusesQuery.data && dragons.length ? (
            <div className="grid gap-4 lg:grid-cols-2">
              {dragons.map(dragon => (
                <DragonCard key={dragon.id} dragon={dragon} bonusDefinitionsById={bonusDefinitionsById} />
              ))}
            </div>
          ) : null}

          {bonusesQuery.data && !dragons.length && !bonusesQuery.isLoading ? (
            <p className="text-sm text-[#b8b8b8]">
              No dragon entries were found in the current age bundle.
            </p>
          ) : null}
        </div>
      </Card>

      <Card className="space-y-5 p-6">
        <div className="space-y-4">
          <h3 className="font-cinzel text-2xl text-[#e6c547]">Slaying</h3>
          <p className="text-[1.0625rem] leading-8 text-[#e3e3e3]">
            Slaying damage depends on the troops or specialists you send. Some bonuses increase damage, and
            some provinces may also use thieves or wizards.
          </p>

          <div className="overflow-x-auto rounded-lg border border-[#d4af37]/25">
            <table className="min-w-full divide-y divide-[#d4af37]/20 text-left text-base">
              <thead className="bg-[#d4af37]/12 text-[#e6c547]">
                <tr>
                  <th className="px-4 py-3 font-semibold">Unit sent</th>
                  <th className="px-4 py-3 font-semibold">Damage rule</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/12 bg-[#0a0a0a]">
                {slayingRows.map(([label, value]) => (
                  <tr key={label}>
                    <td className="px-4 py-3 text-[#f0f0f0]">{label}</td>
                    <td className="px-4 py-3 text-[#f0f0f0]">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-[1.0625rem] leading-8 text-[#e3e3e3]">
            A flying dragon&apos;s starting strength is based on the target kingdom networth and current relation.
            War then applies any dragon strength bonus from the sending kingdom.
          </p>
          <p className="text-[1.0625rem] leading-8 text-[#e3e3e3]">
            Dragonkind takes reduced effect from an enemy dragon. Dragon Charm reduces an active dragon.
          </p>
          <p className="text-[1.0625rem] leading-8 text-[#e3e3e3]">
            Mystery dragons launch at reduced strength and become a random dragon type. Bribed dragons take
            part of an enemy dragon instead of launching a new one.
          </p>
        </div>

        <div className="space-y-3">
          <h3 className="font-cinzel text-2xl text-[#e6c547]">Cost and Funding</h3>
          <ul className="space-y-2 rounded-lg border border-[#d4af37]/15 bg-[#050505]/50 px-5 py-4 text-[1.0625rem] leading-7 text-[#e8dfcf]">
            <li className="flex gap-3"><span className="mt-[0.65rem] h-1.5 w-1.5 shrink-0 rounded-full bg-[#e6c547]" /><span>The gold cost is based on the larger of the source or target kingdom networth.</span></li>
            <li className="flex gap-3"><span className="mt-[0.65rem] h-1.5 w-1.5 shrink-0 rounded-full bg-[#e6c547]" /><span>During war, Halfling aura can lower your own cost and increase the enemy dragon cost.</span></li>
            <li className="flex gap-3"><span className="mt-[0.65rem] h-1.5 w-1.5 shrink-0 rounded-full bg-[#e6c547]" /><span>Dragon Lore style bonuses can make each donated gold piece more efficient.</span></li>
            <li className="flex gap-3"><span className="mt-[0.65rem] h-1.5 w-1.5 shrink-0 rounded-full bg-[#e6c547]" /><span>War-only dragon aider effects improve donation efficiency while kingdoms are actively at war.</span></li>
          </ul>
        </div>

        <div className="space-y-4">
          <h3 className="font-cinzel text-2xl text-[#e6c547]">Related Pages</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <Link to="/classic-guide/$pageId" params={{ pageId: 'kingdom-relations' }}>
              <Card className="h-full p-4 transition-colors hover:border-[#d4af37]/40">
                <h4 className="font-cinzel text-xl text-[#f0f0f0]">Kingdom Relations</h4>
                <p className="mt-2 text-sm text-[#b8b8b8]">Relations, hostility and war.</p>
              </Card>
            </Link>
            <Link to="/classic-guide/$pageId" params={{ pageId: 'kingdom-monarchy' }}>
              <Card className="h-full p-4 transition-colors hover:border-[#d4af37]/40">
                <h4 className="font-cinzel text-xl text-[#f0f0f0]">Kingdom Monarchy</h4>
                <p className="mt-2 text-sm text-[#b8b8b8]">Monarch powers for dragon projects.</p>
              </Card>
            </Link>
            <Link to="/classic-guide/$pageId" params={{ pageId: 'military' }}>
              <Card className="h-full p-4 transition-colors hover:border-[#d4af37]/40">
                <h4 className="font-cinzel text-xl text-[#f0f0f0]">Military</h4>
                <p className="mt-2 text-sm text-[#b8b8b8]">Military values used when slaying.</p>
              </Card>
            </Link>
            <Link to="/actions/$actionId" params={{ actionId: 'dragon_charm' }}>
              <Card className="h-full p-4 transition-colors hover:border-[#d4af37]/40">
                <h4 className="font-cinzel text-xl text-[#f0f0f0]">Dragon Charm</h4>
                <p className="mt-2 text-sm text-[#b8b8b8]">The direct spell against dragons.</p>
              </Card>
            </Link>
          </div>
        </div>
      </Card>
    </div>
  );
}
